import React, { useEffect, useState } from "react";
import { Button, Form, notification, Select, Tag } from "antd";
import { CustomTagProps } from "rc-select/lib/BaseSelect";
import NotificationService from "@Services/NotificationService";
import SessionManager from "@Core/session";
import { IUserConnectionDetails } from "@Models/IUserConnectionDetails";
import { IActivityDetails } from "@Models/IActivityDetails";
import { INotificationModel, INotificationPayload } from "@Models/INotificationPayload";

interface IFormValues {
    users: string[];
}

interface IUserValue {
    label: string;
    value: string;
}

type NotificationPayload = () => Promise<INotificationPayload | { errors: string[] }>;

interface IProps {
    activity: IActivityDetails;
    userConnections?: IUserConnectionDetails[];
}

const VideoInvite: React.FC<IProps> = ({ activity, userConnections }: IProps) => {
    const [userOptions, setUserOptions] = useState<IUserValue[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [sendingInvitation, setSendingInvitation] = useState<boolean>(false);

    const createNotificationPayload: NotificationPayload = async () => {
        const service = new NotificationService();
        const result = await service.createPayload(
            SessionManager.getServiceUser()?.userId,
            "VideoReviewRequest",
            activity.activityInstanceId
        );
        if (!result.hasErrors) {
            return result.value;
        }
        notification.error({ message: "Notification payload error: ", description: result.errors });
        return { errors: result.errors };
    };

    /**
     * Return a user tag to be rendered in the Select box.
     * @param props
     */
    const renderUserTag = (props: CustomTagProps) => {
        const { label, closable, onClose } = props;
        const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
            event.preventDefault();
            event.stopPropagation();
        };
        return (
            <Tag
                color={"blue"}
                onMouseDown={onPreventMouseDown}
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3 }}
            >
                {label}
            </Tag>
        );
    };

    /**
     * Send invite request to users selected in the search field.
     * @param values
     */
    const onSubmitInviteForm = async (values: IFormValues) => {
        setSendingInvitation(true);
        const payload = await createNotificationPayload();

        // Check if there is notification payload error and prevent the invitations request
        if ("errors" in payload) {
            setSendingInvitation(false);
            return;
        }
        const service = new NotificationService();
        const data: INotificationModel[] = values.users.map((recipientId) => ({
            dateCreated: new Date(),
            dateUpdated: new Date(),
            notificationId: "",
            originatorId: SessionManager.getServiceUser()?.userId,
            payload: payload,
            recipientId: recipientId,
            status: "new",
        }));

        try {
            await Promise.all([...data.map((notification) => service.create(notification.originatorId, notification))]);
        } catch (err) {
            notification.error({ message: "Error sending invitation.", description: err });
        }
        notification.success({ message: "Invitation request sent." });
        setUserOptions([]);
        setSendingInvitation(false);
    };

    /**
     * Create options for invite-form Select
     */
    useEffect(() => {
        const newOptions = [];
        if (userConnections) {
            userConnections
                .filter((x) => x.status === "connected")
                .map((user) =>
                    newOptions.push({
                        label: user.fullName,
                        value: user.userId,
                    })
                );
        }
        setUserOptions(newOptions);
    }, [userConnections]);

    return (
        <div className="video-invite">
            <span>Invite your connections to review this video</span>
            <Form
                name="invite-form"
                className="activity-user-connections"
                onFinish={(values: IFormValues) => onSubmitInviteForm(values)}
            >
                <Form.Item name="users">
                    <Select
                        className="activity-users-select"
                        allowClear={true}
                        placeholder={"Type to find users"}
                        filterOption={true}
                        optionFilterProp="label"
                        showArrow
                        mode="multiple"
                        tagRender={renderUserTag}
                        options={userOptions}
                        maxTagCount={20}
                        onChange={(value) => setSelectedUsers(value)}
                        value={selectedUsers.length > 0 ? selectedUsers : null}
                    />
                </Form.Item>
                <Form.Item>
                    <Button
                        loading={sendingInvitation}
                        disabled={selectedUsers.length === 0}
                        className="secondary"
                        htmlType="submit"
                    >
                        Invite
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};
export default VideoInvite;
